import classNames from 'classnames';
import { ThemeWBProps } from 'eco-design-micro/props/const';
import { DataLayerProps } from 'eco-design-micro/props/DataLayerProps';
import gtm from 'eco-design-reliance/gtm';
import { ChangeEvent, useEffect, useState } from 'react';
import IconAdd from './icons/IconAdd';
import IconRed from './icons/IconRed';
import './index.scss';
export interface EcoCalculatorProps {
    /**
     * 主题色
     */
    theme?: ThemeWBProps;
    /**
     * 按钮类型
     */
    type?: 'circle' | 'square' | 'border-radius-20';
    /**
     * 样式类
     */
    className?: string;
    /**
     * 购买数量
     */
    quantity?: number;
    /**
     * 最小购买量
     */
    minQuantity?: number;
    /**
     * 最大购买量
     */
    maxQuantity?: number;
    /**
     * 库存
     */
    inventory?: number;
    /**
     * 变化回调
     */
    onChange?: (quantity: number) => void;
    /**
     * 用于 gtm 埋点
     */
    title?: React.ReactNode;
    /**
     * 用于 gtm 埋点
     */
    price?: number;
    /**
     *  埋点
     */
    dataLayer?: DataLayerProps;
}

/**
 * 按钮用于开始一个即时操作。
 */
export default function EcoCalculator({
    type = 'circle',
    theme = 'white',
    quantity = 0,
    minQuantity = 0,
    maxQuantity = 99999,
    inventory = 99999,
    className,
    onChange,
    title,
    price,
    dataLayer,
}: EcoCalculatorProps) {
    const prefixCls = 'eco-calculator';
    const maxCount = Math.min(maxQuantity, inventory); //最大购买量
    const countEnter = Math.min(maxCount, quantity); //传入的值取哪一个
    const [count, setCount] = useState(countEnter);
    const compCls = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-type-${type}`]: type,
        [`${prefixCls}-l-disabled`]: count <= minQuantity, // 左侧置灰
        [`${prefixCls}-r-disabled`]: count >= maxCount, // 右侧置灰
    });

    // 执行变化
    const countChange = (value: number) => {
        setCount(value);
        if (onChange) {
            onChange(value);
        }
    };

    // 直接输入
    const countHandle = (event: ChangeEvent<HTMLInputElement>) => {
        const value1 = parseInt(event.target.value) || 0;
        const value2 = Math.min(value1, maxCount); //不能大于最大
        const value3 = Math.max(minQuantity, value2); //不能小于0
        countChange(value3);
    };

    // 减1
    const redHandle = () => {
        if (count <= minQuantity) {
            return;
        }
        const value1 = count - 1;
        countChange(value1);
        try {
            if (title && price) {
                const dataLayer2 = dataLayer || {
                    event: 'uaEvent',
                    event_name: 'bundle_remove_from_cart',
                    product_name: title,
                    price: price,
                    quantity: value1,
                    button_name: 'remove',
                    list_name: 'none',
                    tag: 'none',
                };
                gtm.push(dataLayer2);
            }
        } catch (e) {
            console.error(e);
        }
    };

    // 加1
    const addHandle = () => {
        if (count >= maxCount) {
            return;
        }
        const value1 = count + 1;
        countChange(value1);

        try {
            if (title && price) {
                const dataLayer2 = dataLayer || {
                    event: 'uaEvent',
                    event_name: 'bundle_add_to_cart',
                    product_name: title,
                    price: price,
                    quantity: value1,
                    button_name: 'add',
                    list_name: 'none',
                    tag: 'none',
                };
                gtm.push(dataLayer2);
            }
        } catch (e) {
            console.error(e);
        }
    };

    useEffect(() => {
        if (count !== countEnter) {
            setCount(countEnter);
        }
    }, [countEnter]);

    return (
        <div className={compCls}>
            <div className={`${prefixCls}-btn ${prefixCls}-btn-red`} onClick={redHandle}>
                <IconRed />
            </div>
            <input type="text" className={`${prefixCls}-input`} value={count} onChange={countHandle} />
            <div className={`${prefixCls}-btn ${prefixCls}-btn-add`} onClick={addHandle}>
                <IconAdd />
            </div>
        </div>
    );
}
